import { ChartOfAccountsRepository } from '~/repositories/chartOfAccountsRepository';
import { leaseRepository } from '~/repositories/leaseRepository';
import { propertiesRepository } from '~/repositories/propertiesRepository';
import { CompanyPortalTransactionRepository } from '~/repositories/transactionRepository.companyPortal';
import { unitRepository } from '~/repositories/unitRepository';
import { validateId } from '~/repositories/utils';
import { resolveVendorsByIds } from '~/repositories/vendorRepository';
import { VendorBillSourceType } from '~/swagger/accounting/Api';
import { envConfig } from '~/utils/env';
import { urlWithQuery } from '~/utils/url';
import { BaseRestRepository } from './baseRestRepository';
import { uploadSessionRepository } from './uploadSessionRepository';
import type { UploadFileOptions } from './uploadSessionRepository';
import type { CompanyPortalTransactionDto } from '~/repositories/transactionRepository.companyPortal';
import type {
  AddVendorBillFilesRequestDto,
  CreateVendorBillRequestDto,
  DeleteVendorBillFilesRequestDto,
  DeleteVendorBillRequestDto,
  GetVendorBillsParams,
  SetVendorBillSourceRequestDto,
  UpdateVendorBillRequestDto,
  VendorBillDto,
  VendorBillPaginationDto,
} from '~/swagger/accounting/Api';
import type { FileDto } from '~/swagger/files/Api';
import type {
  ChartOfAccountDto,
  HydratedLeaseDto,
  HydratedPropertyDto,
  HydratedUnitDto,
  VendorDto,
  EstimateVendorBillAchArrivalTimesDto,
  EstimateVendorBillAchArrivalTimesResultDto,
  GetVendorBillsToPayParams,
  GetVendorBillsToSettleParams,
  PayBillsResultsDto,
  PayVendorBillsDto,
  SettleVendorBillsDto,
  SettleVendorBillsResultDto,
  VendorBillsToPayDto,
  VendorBillsToSettleDto,
} from '~/swagger/portal/Api';

const BILLS_URL = `${envConfig.service.accounting}/company-portal/vendor-bills`;
const PAY_BILLS_URL = '/api/vendors/bills';

export type AddBillFilePayload = {
  uploadSessionId: string;
  description?: string;
};

type BillReferences = {
  properties: HydratedPropertyDto[];
  units: HydratedUnitDto[];
  chartOfAccounts: ChartOfAccountDto[];
};

export type HydratedVendorBill = VendorBillDto &
  BillReferences & {
    vendor?: VendorDto;
    leases: HydratedLeaseDto[];
    transactions: CompanyPortalTransactionDto[];
    hydratedFiles: FileDto[];
  };

export type HydratedVendorBillPagination = VendorBillPaginationDto &
  BillReferences & {
    vendors: VendorDto[];
  };

const chartOfAccountsRepository = new ChartOfAccountsRepository();
const companyPortalTransactionRepository = new CompanyPortalTransactionRepository();

const uniqueIds = (ids: (string | null | undefined)[]): string[] => [...new Set(ids.filter((id): id is string => !!id))];

const fetchByIds = <T>(ids: string[], fetch: (ids: string[]) => Promise<T[]>): Promise<T[]> =>
  ids.length ? fetch(ids) : Promise.resolve<T[]>([]);

export class BillRepository extends BaseRestRepository {
  public async getBills(filter?: GetVendorBillsParams): Promise<HydratedVendorBillPagination> {
    const url = urlWithQuery(BILLS_URL, filter);
    const response = await this.fetchWithAuth(url);
    const pagination: VendorBillPaginationDto = await this.getJsonResponse(response);

    const [references, vendors] = await Promise.all([
      this.hydrateReferences(pagination.items),
      resolveVendorsByIds(uniqueIds(pagination.items.map((bill) => bill.vendorId))),
    ]);

    return { ...pagination, ...references, vendors };
  }

  public async getBill(billId: string): Promise<HydratedVendorBill> {
    validateId(billId);
    const url = `${BILLS_URL}/${billId}`;
    const response = await this.fetchWithAuth(url);
    const bill: VendorBillDto = await this.getJsonResponse(response);

    const transactionIds = uniqueIds(
      bill.lineItems.flatMap((lineItem) => lineItem.allocations.map((allocation) => allocation.transactionId))
    );

    const [references, vendors, leases, transactions, hydratedFiles] = await Promise.all([
      this.hydrateReferences([bill]),
      resolveVendorsByIds([bill.vendorId]),
      fetchByIds(uniqueIds(bill.lineItems.map((lineItem) => lineItem.leaseId)), (ids) => leaseRepository.batchGetLeasesByIds(ids)),
      Promise.all(transactionIds.map((transactionId) => companyPortalTransactionRepository.getTransaction(transactionId))),
      this.hydrateFiles(bill.files.map((file) => file.fileId)),
    ]);

    return { ...bill, ...references, vendor: vendors[0], leases, transactions, hydratedFiles };
  }

  private async hydrateFiles(fileIds: string[]): Promise<FileDto[]> {
    if (fileIds.length === 0) return [];
    const url = urlWithQuery(`${envConfig.service.files}/files`, { ids: fileIds });
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  private async hydrateReferences(bills: VendorBillDto[]): Promise<BillReferences> {
    const lineItems = bills.flatMap((bill) => bill.lineItems);
    const propertyIds = uniqueIds(lineItems.map((lineItem) => lineItem.propertyId));
    const unitIds = uniqueIds(lineItems.map((lineItem) => lineItem.unitId));

    const [properties, units, chartOfAccounts] = await Promise.all([
      fetchByIds(propertyIds, (ids) => propertiesRepository.batchGetPropertyByIds(ids)),
      fetchByIds(unitIds, (ids) => unitRepository.batchGetUnitsByIds(ids)),
      lineItems.length ? chartOfAccountsRepository.getChartOfAccounts() : Promise.resolve<ChartOfAccountDto[]>([]),
    ]);

    return { properties, units, chartOfAccounts };
  }

  public async createBill(bill: CreateVendorBillRequestDto): Promise<VendorBillDto> {
    const url = BILLS_URL;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(bill),
    });
    return this.getJsonResponse(response);
  }

  public async updateBill(billId: string, bill: UpdateVendorBillRequestDto): Promise<VendorBillDto> {
    validateId(billId);
    const url = `${BILLS_URL}/${billId}`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(bill),
    });
    return this.getJsonResponse(response);
  }

  public async setBillSource(billId: string, workOrderId: string | undefined): Promise<VendorBillDto> {
    validateId(billId);
    const url = `${BILLS_URL}/${billId}/source`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify({
        sourceType: workOrderId == null ? undefined : VendorBillSourceType.WorkOrder,
        sourceId: workOrderId,
      } satisfies SetVendorBillSourceRequestDto),
    });
    return this.getJsonResponse(response);
  }

  public async deleteBill(billId: string, payload: DeleteVendorBillRequestDto = { adjustPayments: true }): Promise<void> {
    validateId(billId);
    const url = `${BILLS_URL}/${billId}`;

    await this.fetchWithAuth(url, {
      method: 'DELETE',
      body: JSON.stringify(payload),
    });
  }

  public async payBill(payload: PayVendorBillsDto): Promise<PayBillsResultsDto> {
    const url = `${PAY_BILLS_URL}/pay`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async deleteBillItem(billId: string, paymentId: string): Promise<void> {
    validateId(billId);
    const url = `${BILLS_URL}/${billId}/payments/${paymentId}`;
    await this.fetchWithAuth(url, {
      method: 'DELETE',
    });
  }

  public async addBillFile(billId: string, files: AddBillFilePayload[]): Promise<VendorBillDto> {
    const url = `${BILLS_URL}/${billId}/files`;
    const payload: AddVendorBillFilesRequestDto = { files };

    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return this.getJsonResponse(response);
  }

  public async uploadBillFiles(billId: string, files: File[], opts?: UploadFileOptions): Promise<string[]> {
    validateId(billId, 'Bill');
    if (files.length === 0) return [];

    return uploadSessionRepository.uploadFiles(`${BILLS_URL}/${billId}/files/upload-sessions`, files, opts);
  }

  public async deleteBillFiles(billId: string, fileIds: string[]): Promise<void> {
    const url = `${BILLS_URL}/${billId}/files`;
    const payload: DeleteVendorBillFilesRequestDto = {
      fileIds,
    };
    await this.fetchWithAuth(url, {
      method: 'DELETE',
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  public async getVendorBillsPay(filter?: GetVendorBillsToPayParams): Promise<VendorBillsToPayDto> {
    const url = urlWithQuery(`${PAY_BILLS_URL}/pay`, filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getVendorBillsSettle(filter?: GetVendorBillsToSettleParams): Promise<VendorBillsToSettleDto> {
    const url = urlWithQuery(`${PAY_BILLS_URL}/settle`, filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async settleVendorBills(payload: SettleVendorBillsDto): Promise<SettleVendorBillsResultDto> {
    const url = `${PAY_BILLS_URL}/settle`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async estimateVendorBillAchArrivalTimes(
    payload: EstimateVendorBillAchArrivalTimesDto
  ): Promise<EstimateVendorBillAchArrivalTimesResultDto> {
    const url = `${PAY_BILLS_URL}/pay/estimate`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }
}

export const billRepository = new BillRepository();
